import { chrome, pages, type PageMeta } from './content';

type Tool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations: { readOnlyHint: boolean };
  execute: (input: Record<string, unknown>) => Promise<unknown>;
};

type ModelContext = {
  provideContext?: (context: { tools: Tool[] }) => void;
  clearContext?: () => void;
  registerTool?: (tool: Tool, options?: { signal: AbortSignal }) => void | Promise<void>;
  unregisterTool?: (name: string) => void;
};

const publicPages = pages.filter(
  (page) => page.status === 'publish' && !/noindex/i.test(page.seo.robots ?? '') &&
    !['/homepage-main', '/sales-dashboard', '/my-instagram-feed'].includes(page.route),
);

type PublicContentEntry = {
  path: string;
  title: string;
  description: string;
  canonical: string;
  markdownPath: string;
  markdownTokens: number;
  headings: string[];
  sources: string[];
  sourceOccurrences?: Array<{
    occurrence: number;
    label: string;
    href: string;
    url: string;
    heading: string | null;
  }>;
  searchText: string;
};

let contentIndexRequest: Promise<PublicContentEntry[]> | null = null;

async function contentIndex(): Promise<PublicContentEntry[]> {
  contentIndexRequest ??= fetch('/content-index.json', {
    headers: { Accept: 'application/json' },
  }).then(async (response) => {
    if (!response.ok) throw new Error(`Public content index request failed (${response.status}).`);
    const data = await response.json() as { pages?: unknown };
    if (!Array.isArray(data.pages)) throw new Error('Public content index has an invalid shape.');
    return data.pages as PublicContentEntry[];
  }).catch((error) => {
    contentIndexRequest = null;
    throw error;
  });
  return contentIndexRequest;
}

async function publicEntry(path: string): Promise<PublicContentEntry> {
  const entry = (await contentIndex()).find((candidate) => candidate.path === path);
  if (!entry) throw new Error('Unknown or non-public page. Use an exact path returned by search.');
  return entry;
}

async function pageMarkdown(entry: PublicContentEntry): Promise<string> {
  const response = await fetch(entry.path, { headers: { Accept: 'text/markdown' } });
  if (!response.ok) throw new Error(`Markdown request failed (${response.status}).`);
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().startsWith('text/markdown')) {
    throw new Error('The server did not return the requested Markdown representation.');
  }
  return response.text();
}

function text(html: string): string {
  const element = document.createElement('div');
  element.innerHTML = html;
  return element.textContent?.trim() ?? '';
}

function summary(page: PageMeta) {
  return {
    title: text(page.title),
    path: page.route,
    description: text(page.seo.description || page.teaser || page.excerpt),
  };
}

function requiredString(input: Record<string, unknown>, key: string): string {
  const value = input?.[key];
  if (typeof value !== 'string' || !value.trim() || value.length > 200) {
    throw new Error(`${key} must be a non-empty string of at most 200 characters.`);
  }
  return value.trim();
}

function boundedInteger(
  input: Record<string, unknown>,
  key: string,
  fallback: number,
  min: number,
  max: number,
): number {
  const value = input?.[key];
  if (value === undefined) return fallback;
  if (!Number.isInteger(value) || (value as number) < min || (value as number) > max) {
    throw new Error(`${key} must be an integer from ${min} to ${max}.`);
  }
  return value as number;
}

function normalized(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase('de-CH')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

function relevance(page: PublicContentEntry, terms: string[], phrase: string): number {
  const fields = [
    [page.title, 12],
    [page.headings.join(' '), 7],
    [page.description, 5],
    [page.path, 4],
    [page.sourceOccurrences?.map((source) => `${source.label} ${source.url}`).join(' ') ?? page.sources.join(' '), 3],
    [page.searchText, 1],
  ] as const;
  let score = 0;
  for (const [field, weight] of fields) {
    const value = normalized(field);
    for (const term of terms) if (value.includes(term)) score += weight;
    if (phrase.length > 2 && value.includes(phrase)) score += weight * 2;
  }
  return score;
}

function result(data: unknown) {
  return { content: [{ type: 'text', text: JSON.stringify(data) }] };
}

export function siteTools(): Tool[] {
  return [
    {
      name: 'search_health_prime',
      description: 'Search published Health Prime pages about corporate health, screenings, workshops and BGM dossiers. Returns titles, paths and summaries, not medical advice.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', minLength: 1, maxLength: 200 },
          offset: { type: 'integer', minimum: 0, maximum: 10000, default: 0 },
          limit: { type: 'integer', minimum: 1, maximum: 50, default: 10 },
        },
        required: ['query'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const query = normalized(requiredString(input, 'query'));
        const terms = query.split(/\s+/).filter(Boolean);
        if (!terms.length) throw new Error('query must contain at least one letter or number.');
        const offset = boundedInteger(input, 'offset', 0, 0, 10000);
        const limit = boundedInteger(input, 'limit', 10, 1, 50);
        const matches = (await contentIndex()).map((page) => {
          const haystack =
            `${page.title} ${page.description} ${page.path} ${page.headings.join(' ')} ` +
            `${page.sourceOccurrences?.map((source) => `${source.label} ${source.url}`).join(' ') ?? page.sources.join(' ')} ${page.searchText}`;
          return { page, haystack: normalized(haystack), score: relevance(page, terms, query) };
        }).filter((match) => terms.every((term) => match.haystack.includes(term)))
          .sort((a, b) => b.score - a.score || a.page.title.localeCompare(b.page.title, 'de-CH'));
        const pageSlice = matches.slice(offset, offset + limit);
        return result({
          total: matches.length,
          offset,
          limit,
          returned: pageSlice.length,
          hasMore: offset + pageSlice.length < matches.length,
          nextOffset: offset + pageSlice.length < matches.length ? offset + pageSlice.length : null,
          pages: pageSlice.map(({ page: { path, title, description, markdownPath, headings }, score }) => ({
            path, title, description, markdownPath, headings,
            relevance: score,
          })),
        });
      },
    },
    {
      name: 'get_health_prime_page_markdown',
      description: 'Retrieve the complete Markdown main content of an exact public Health Prime page, including dossier cards, quotes, tables, citations and source links.',
      inputSchema: {
        type: 'object',
        properties: { path: { type: 'string', minLength: 1, maxLength: 200 } },
        required: ['path'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const entry = await publicEntry(requiredString(input, 'path'));
        return result({
          path: entry.path,
          canonical: entry.canonical,
          markdownTokens: entry.markdownTokens,
          markdown: await pageMarkdown(entry),
        });
      },
    },
    {
      name: 'get_health_prime_sources',
      description: 'Retrieve citation labels and source URLs from the complete Markdown for an exact public Health Prime page.',
      inputSchema: {
        type: 'object',
        properties: { path: { type: 'string', minLength: 1, maxLength: 200 } },
        required: ['path'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const entry = await publicEntry(requiredString(input, 'path'));
        let sources = entry.sourceOccurrences;
        if (!sources) {
          const markdown = await pageMarkdown(entry);
          let heading: string | null = null;
          let occurrence = 0;
          sources = markdown.split('\n').flatMap((line) => {
            const headingMatch = /^#{1,6}\s+(.+)$/.exec(line);
            if (headingMatch) heading = headingMatch[1];
            return [...line.matchAll(/\[([^\]]+)\]\(([^)\s]+)\)/g)]
              .filter((match) => !match[2].startsWith('#'))
              .map((match) => ({
                occurrence: ++occurrence,
                label: match[1],
                href: match[2],
                url: new URL(match[2], entry.canonical).href,
                heading,
              }));
          });
        }
        return result({ path: entry.path, total: sources.length, sources });
      },
    },
    {
      name: 'get_health_prime_contact',
      description: 'Get Health Prime AG’s public postal address, email and telephone. Does not send a message or submit a form.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true },
      async execute() {
        return result({ ...chrome.footer.address, legalPath: '/impressum' });
      },
    },
    {
      name: 'open_health_prime_page',
      description: 'Navigate this browser tab to a published Health Prime page using an exact path returned by search_health_prime. External URLs and private pages are not allowed.',
      inputSchema: {
        type: 'object',
        properties: { path: { type: 'string', minLength: 1, maxLength: 200 } },
        required: ['path'],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: false },
      async execute(input) {
        const path = requiredString(input, 'path');
        const indexed = await publicEntry(path);
        const page = publicPages.find((candidate) => candidate.route === path);
        window.history.pushState(null, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
        return result({
          navigationRequested: true,
          title: page ? summary(page).title : indexed.title,
          path: indexed.path,
          description: page ? summary(page).description : indexed.description,
        });
      },
    },
  ];
}

/** Feature-detected support for early navigator APIs and the current draft. */
export async function installWebMcp(): Promise<() => void> {
  const navContext = (navigator as Navigator & { modelContext?: ModelContext }).modelContext;
  const docContext = (document as Document & { modelContext?: ModelContext }).modelContext;
  const context = navContext ?? docContext;
  if (!context) return () => {};
  const tools = siteTools();
  const controller = new AbortController();
  const registered: string[] = [];
  let provided = false;
  const cleanup = () => {
    controller.abort();
    if (provided) context.clearContext?.();
    else registered.forEach((name) => context.unregisterTool?.(name));
  };
  try {
    if (context.provideContext) {
      context.provideContext({ tools });
      provided = true;
    } else if (context.registerTool) {
      for (const tool of tools) {
        await context.registerTool(tool, { signal: controller.signal });
        registered.push(tool.name);
      }
    }
  } catch (error) {
    cleanup();
    throw error;
  }
  return cleanup;
}